import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import { getIcpLimit, isUnlimitedIcp } from '../../utils/planLimits';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export interface CreateIcpModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

interface OrgInfo {
  id: string;
  plan: string;
  icpLimit?: number | null;
}

const icpPlaceholder = `Describe your audience so posts feel native to them. Include:

- Voice & tone: casual but informed; mixes industry terms with approachable humor
- Values & worldview: cares about clear results and practical wins; wary of hype without proof
- Jargon & references: uses terms like MQLs, CAC, LTV; mentions tools like HubSpot or GA4
- Praise triggers: loves crisp metrics, actionable tips, time-saving tools
- Criticism triggers: rolls eyes at vague claims, buzzwords, or unrealistic timelines

Be specific and concrete so ideas can mirror how they actually speak.`;

const CreateIcpModal: React.FC<CreateIcpModalProps> = ({ open, onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [data, setData] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [orgInfo, setOrgInfo] = useState<OrgInfo | null>(null);
  const [customCount, setCustomCount] = useState<number>(0);
  const [checking, setChecking] = useState<boolean>(false);

  const reset = () => {
    setTitle('');
    setData('');
    setError(null);
    setLoading(false);
  };

  const fetchOrgAndIcpUsage = async () => {
    try {
      setChecking(true);
      const orgResp = await axios.get(`${API_URL}/api/organization`, {
        withCredentials: true
      });
      const icpResp = await axios.get(`${API_URL}/api/icps`, {
        withCredentials: true
      });
      setOrgInfo(orgResp.data.organization || null);
      const icps = icpResp.data.icps || [];
      const custom = icps.filter((i: any) => !i.default).length;
      setCustomCount(custom);
    } catch (e) {
      // Silent; modal still can be used but submission will validate
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (open) fetchOrgAndIcpUsage();
  }, [open]);

  const handleClose = () => {
    if (!loading) {
      reset();
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedTitle = title.trim();
    const trimmedData = data.trim();
    if (!trimmedTitle || !trimmedData) {
      setError('Title and Summary are required');
      return;
    }

    // Enforce limits for custom ICPs (org limit preferred; fallback to plan when null)
    if (orgInfo) {
      const hasOrgLimit = orgInfo.icpLimit != null;
      const limit = hasOrgLimit ? (orgInfo.icpLimit as number) : getIcpLimit(orgInfo.plan);
      const unlimited = hasOrgLimit ? orgInfo.icpLimit === Number.MAX_SAFE_INTEGER : isUnlimitedIcp(orgInfo.plan);
      if (!unlimited && customCount >= limit) {
        setError('You have reached your custom Persona limit for your organization.');
        return;
      }
    }

    try {
      setLoading(true);
      await axios.post(
        `${API_URL}/api/icps`,
        { title: trimmedTitle, data: trimmedData },
        { withCredentials: true }
      );

      reset();
      if (onCreated) onCreated();
      onClose();
    } catch (err: any) {
      const message = err?.response?.data?.error || err.message || 'Failed to create Persona';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const effectiveIsUnlimited = orgInfo
    ? (orgInfo.icpLimit != null ? orgInfo.icpLimit === Number.MAX_SAFE_INTEGER : isUnlimitedIcp(orgInfo.plan))
    : false;
  const effectiveLimit = orgInfo
    ? (orgInfo.icpLimit != null ? (orgInfo.icpLimit as number) : getIcpLimit(orgInfo.plan))
    : 0;
  const planLimitText = orgInfo
    ? (effectiveIsUnlimited
      ? 'Unlimited custom Personas on your plan.'
      : `Custom Personas: ${customCount} / ${effectiveLimit}`)
    : '';

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Create Persona</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {checking && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CircularProgress size={18} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">Checking plan limits…</Typography>
            </Box>
          )}

          {planLimitText && (
            <Alert severity="info" sx={{ mb: 2 }}>{planLimitText}</Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoFocus
            disabled={loading}
            placeholder="e.g., B2B SaaS Marketing Leaders (50–250 employees, North America)"
          />

          <TextField
            label="Summary"
            value={data}
            onChange={(e) => setData(e.target.value)}
            fullWidth
            margin="normal"
            required
            disabled={loading}
            placeholder={icpPlaceholder}
            minRows={6}
            multiline
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>Cancel</Button>
          <Box sx={{ position: 'relative' }}>
            <Button type="submit" variant="contained" disabled={loading}>Create Persona</Button>
            {loading && (
              <CircularProgress size={24} sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-12px', ml: '-12px' }} />
            )}
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateIcpModal;


